'use client';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { Route } from './+types/contact';
import { SITE } from '~/data/site';
import { useBranding, brandingFromMatches } from '~/lib/branding';
import { buildMeta, breadcrumbSchema } from '~/lib/seo';
import { fireConversion } from '~/lib/gtag';
import { isValidPhone } from '@shared/utils';

// `meta` runs at module scope — no hooks, no per-request tenant lookup of its
// own. `matches` carries the root route's already-resolved branding, so this
// reads the same tenant `useBranding()` reads elsewhere on the page, instead
// of the hardcoded "New Leaf Cleaning" this used to print into every other
// tenant's page title and Open Graph description.
export const meta: Route.MetaFunction = ({ matches }) => {
  const branding = brandingFromMatches(matches);
  return buildMeta({
    title: 'Contact',
    description: `Reach ${branding.name} by email or the contact form. One human, no call centers.${branding.isNewLeaf ? ' Greater Tampa Bay.' : ''}`,
    path: '/contact',
    siteName: branding.name,
    jsonLd: breadcrumbSchema([
      { name: 'Home', url: `${SITE.url}/` },
      { name: 'Contact', url: `${SITE.url}/contact` },
    ]),
  });
};

function ContactForm() {
  const navigate = useNavigate();
  const { name: brandName } = useBranding();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);

  function validate(data: FormData) {
    const e: Record<string, string> = {};
    if (!String(data.get('name') ?? '').trim()) e.name = 'Name is required.';
    const phone = String(data.get('phone') ?? '').trim();
    if (!phone) e.phone = 'Phone is required.';
    else if (!isValidPhone(phone)) e.phone = 'Enter a valid 10-digit phone number.';
    const email = String(data.get('email') ?? '').trim();
    if (!email) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email.';
    if (!consent) e.consent = 'Please agree to be contacted so we can reach out.';
    return e;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const errs = validate(data);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStatus('submitting');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: String(data.get('name') ?? '').trim(),
          phone: String(data.get('phone') ?? '').trim(),
          email: String(data.get('email') ?? '').trim() || undefined,
          message: String(data.get('message') ?? '').trim() || undefined,
          source: 'contact-page',
        }),
      });
      if (!res.ok) throw new Error('submit failed');
      // Fire on SUCCESS, not on click. As an onClick it fired even when the
      // submit failed, so Google Ads was optimising against leads that never
      // arrived. /thank-you no longer fires it, so this is counted once.
      fireConversion();
      void navigate('/thank-you');
    } catch {
      setStatus('error');
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="quote-form-grid">
        <label className="form-label">
          <span className="form-label-text">Your name</span>
          <input name="name" className={`form-input${errors.name ? ' invalid' : ''}`} />
          {errors.name && <span className="form-error-msg">{errors.name}</span>}
        </label>
        <label className="form-label">
          <span className="form-label-text">Phone</span>
          <input name="phone" type="tel" className={`form-input${errors.phone ? ' invalid' : ''}`} />
          {errors.phone && <span className="form-error-msg">{errors.phone}</span>}
        </label>
      </div>
      <label className="form-label form-field">
        <span className="form-label-text">Email</span>
        <input name="email" type="email" className={`form-input${errors.email ? ' invalid' : ''}`} />
        {errors.email && <span className="form-error-msg">{errors.email}</span>}
      </label>
      <label className="form-label form-field">
        <span className="form-label-text">What can we help with?</span>
        <textarea name="message" className="form-input" rows={4} />
      </label>
      <label className="form-consent">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>
          I agree to receive a call or text from {brandName} at the number above.
          Consent not required for purchase. Msg/data rates may apply. See our{' '}
          <a href="/policies#privacy" target="_blank" rel="noopener noreferrer">
            privacy policy
          </a>
          .
        </span>
      </label>
      {errors.consent && <span className="form-error-msg">{errors.consent}</span>}
      {/* `status` was set to 'error' on a failed POST but rendered nowhere, so
          the visitor watched the button go back to "Send" and assumed it went
          through. On the page literally called Contact. */}
      {status === 'error' && (
        <p className="form-error-msg form-error-banner" role="alert">
          That didn&rsquo;t send &mdash; something went wrong on our end. Please try
          again, or reach us directly at{' '}
          <a href={`mailto:${SITE.contact.email}`}>{SITE.contact.email}</a>.
        </p>
      )}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary btn-lg" disabled={status === 'submitting' || !consent}>
          {status === 'submitting' ? 'Sending…' : 'Send →'}
        </button>
        <span className="marginalia">We read every one.</span>
      </div>
    </form>
  );
}

export default function ContactPage() {
  // This tenant's own contact details — SITE is New Leaf's. See ~/lib/branding.
  const branding = useBranding();
  return (
    <main>
      <section className="section">
        <div className="container-narrow">
          {/* Contact card leads the page — the three actions are the reason
              anyone lands here, so they sit above the headline copy. Each is a
              real hotlink: mailto:. */}
          <div className="leaf-card contact-card">
            <p className="contact-card-lead">Email us, or use the form below.</p>
            {/*
              Each action renders only if THIS company has that detail. The page
              used to print New Leaf's number and address to every tenant's
              visitors — a wrong contact is worse than a missing one, because
              someone acts on it and reaches a business that has never heard of
              them. `branding.phoneHref` is null exactly when `phone` is.
            */}
            <div className="contact-actions">
              {branding.email && (
                <a href={`mailto:${branding.email}`} className="btn btn-ghost contact-action">
                  Email {branding.email}
                </a>
              )}
            </div>
            <p className="contact-card-detail">
              {SITE.contact.hours}
            </p>
          </div>

          <p className="eyebrow page-eyebrow contact-eyebrow-mt">The easiest way to reach us</p>
          <h1 className="display-lg page-h1-full">
            One human,{' '}
            <em className="italic-display">at this address</em>.
          </h1>
          <p className="lede chapter-lede">
            No call center. No outsourced support. When you write, you reach us
            &mdash; usually within ten minutes.
          </p>
        </div>
      </section>

      <section className="section-sm bg-bone-warm">
        <div className="container-narrow">
          <h2 className="display-md">Or send us a note.</h2>
          <div className="form-section-mt">
            <ContactForm />
          </div>
        </div>
      </section>
    </main>
  );
}
